import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { AuthContext } from '../../context/AuthContext';
import CircularProgress from '@mui/material/CircularProgress';
import "./styles/AddUser.scss"
import {
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    Autocomplete,
    Chip,
    Box,
    Typography,
    Alert,
    Skeleton
} from '@mui/material';
import {
    collection,
    addDoc,
    updateDoc,
    doc,
    arrayUnion,
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import {
    fetchAdmins,
    fetchUserData,
    fetchClientesPorAdministrativo,
    fetchTiendasPorCampoCliente,
    fetchClientesPorInspector,
    fetchClientesPaginados,
    fetchTiendasPaginadas,
    buscarClientes,
    buscarTiendas,
    getEstadisticas
} from './firestoreService';
import { validateEmail } from './utils/validate';
import { comunidades, provinciasPorComunidad, getProvinciaFromCodigo, getComunidadFromCodigoProvincia } from "../../zoneData.js"
import ProvinciasSelect from './utils/ProvinciasSelect.js';
import { procesarClientesFreematica } from '../../utils/ClienteSearch';

// Hook personalizado para debounce
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

const AddUser = () => {
    const { currentService, currentUser, adminData } = useContext(AuthContext);
    const [clientes, setClientes] = useState([]);
    const [tiendas, setTiendas] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingUser, setIsLoadingUser] = useState(false);
    const [userData, setUserData] = useState({});
    const [admins, setAdmins] = useState([]);
    const [clienteSeleccionado, setClienteSeleccionado] = useState('');
    const [isAllClientesSelected, setIsAllClientesSelected] = useState(false);
    const [isAllTiendasSelected, setIsAllTiendasSelected] = useState(false);
    const [tiendasFiltradas, setTiendasFiltradas] = useState([]);
    const [comunidadesSeleccionadas, setComunidadesSeleccionadas] = useState([]);
    const [provinciasSeleccionadas, setProvinciasSeleccionadas] = useState([]);
    const [clientesFiltrados, setClientesFiltrados] = useState([]);
    const [estadisticas, setEstadisticas] = useState({ totalClientes: 0, totalTiendas: 0 });
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [hasMoreClientes, setHasMoreClientes] = useState(true);
    const [hasMoreTiendas, setHasMoreTiendas] = useState(true);
    const [lastClienteDoc, setLastClienteDoc] = useState(null);
    const [lastTiendaDoc, setLastTiendaDoc] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    
    const [newUserData, setNewUserData] = useState({
        nombre: '',
        apellido: '',
        correo: '',
        password: '',
        tipoUsuario: [],
        clientes: [],
        tiendas: [],
        provincias: [],
        comunidades: [],
    });

    const navigate = useNavigate();
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    // Cargar datos iniciales de forma optimizada
    useEffect(() => {
        if (currentUser && currentService) {
            setIsLoading(true);
            
            // Cargar datos en paralelo
            Promise.all([
                fetchAdmins(currentService),
                fetchUserData(currentUser, currentService),
                getEstadisticas(currentService)
            ])
            .then(([adminsData, userDataResult, stats]) => {
                setAdmins(adminsData);
                setUserData(userDataResult);
                setEstadisticas(stats);
            })
            .catch(console.error)
            .finally(() => setIsLoading(false));
        }
    }, [currentUser, currentService]);

    // Cargar clientes según el tipo de usuario
    useEffect(() => {
        if (currentUser && currentService && userData) {
            const loadClientes = async () => {
                try {
                    let clientesData = [];
                    
                    if (userData?.tipoUsuario?.includes('administrativo')) {
                        clientesData = await fetchClientesPorAdministrativo(userData.id, currentService);
                    } else if (userData?.tipoUsuario?.includes('inspector')) {
                        clientesData = await fetchClientesPorInspector(userData.id, currentService);
                    } else if (userData?.tipoUsuario?.includes('administrador')) {
                        // Para administradores, cargar solo la primera página
                        const result = await fetchClientesPaginados(currentService, 50);
                        clientesData = result.clientes;
                        setHasMoreClientes(result.hasMore);
                        setLastClienteDoc(result.lastDoc);
                    }
                    
                    const clientesProcesados = procesarClientesFreematica(clientesData);
                    setClientes(clientesProcesados);
                    setClientesFiltrados(clientesProcesados);
                } catch (error) {
                    console.error('Error loading clientes:', error);
                }
            };
            
            loadClientes();
        }
    }, [currentUser, currentService, userData]);

    // Función para cargar más clientes (paginación)
    const loadMoreClientes = useCallback(async () => {
        if (!hasMoreClientes || !currentService || isLoadingMore) return;
        
        setIsLoadingMore(true);
        try {
            const result = await fetchClientesPaginados(currentService, 50, lastClienteDoc);
            const nuevosClientes = procesarClientesFreematica(result.clientes);
            
            setClientes(prev => [...prev, ...nuevosClientes]);
            setClientesFiltrados(prev => [...prev, ...nuevosClientes]);
            setHasMoreClientes(result.hasMore);
            setLastClienteDoc(result.lastDoc);
        } catch (error) {
            console.error('Error loading more clientes:', error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [hasMoreClientes, currentService, lastClienteDoc, isLoadingMore]);

    // Función para buscar clientes con debounce
    const handleSearchClientes = useCallback(async (searchTerm) => {
        if (!currentService || searchTerm.length < 2) {
            setClientesFiltrados(clientes);
            return;
        }
        
        setIsSearching(true);
        try {
            const clientesEncontrados = await buscarClientes(currentService, searchTerm, 50);
            const clientesProcesados = procesarClientesFreematica(clientesEncontrados);
            setClientesFiltrados(clientesProcesados);
        } catch (error) {
            console.error('Error searching clientes:', error);
        } finally {
            setIsSearching(false);
        }
    }, [currentService, clientes]);

    // Efecto para manejar la búsqueda con debounce
    useEffect(() => {
        handleSearchClientes(debouncedSearchTerm);
    }, [debouncedSearchTerm, handleSearchClientes]);

    // Cargar tiendas cuando se selecciona un cliente
    useEffect(() => {
        if (clienteSeleccionado && currentService) {
            const loadTiendas = async () => {
                try {
                    const result = await fetchTiendasPaginadas(currentService, 100, null, { cliente: clienteSeleccionado });
                    setTiendas(result.tiendas);
                    setTiendasFiltradas(result.tiendas);
                    setHasMoreTiendas(result.hasMore);
                    setLastTiendaDoc(result.lastDoc);
                } catch (error) {
                    console.error('Error loading tiendas:', error);
                }
            };
            
            loadTiendas();
        }
    }, [clienteSeleccionado, currentService]);

    const handleAddUser = async () => {
        if (!validateEmail(newUserData.correo)) {
            alert('Por favor, introduce una dirección de correo electrónico válida.');
            return;
        }

        if (newUserData.password.length < 6) {
            alert('La contraseña debe tener al menos 6 caracteres.');
            return;
        }
        
        try {
            setIsLoadingUser(true);
            
            if (userData.tipoUsuario.includes('administrador')) {
                await addNewUser();
            } else if (userData.tipoUsuario.includes('administrativo')) {
                if (!newUserData.tipoUsuario.includes('administrador')) {
                    await addNewUser();
                } else {
                    alert('Los administrativos no pueden añadir administradores');
                    return;
                }
            } else if (userData.tipoUsuario.includes('inspector')) {
                if (newUserData.tipoUsuario.includes('inspector') && newUserData.tipoUsuario.length === 1) {
                    await addNewUser();
                } else {
                    alert('Los inspectores solo pueden añadir inspectores');
                    return;
                }
            }
            
            navigate(-1);
        } catch (error) {
            alert("Error al añadir al usuario");
            console.error('Error al registrar el nuevo usuario:', error);
        } finally {
            setIsLoadingUser(false);
        }
    };

    const addNewUser = async () => {
        try {
            const lowercaseEmail = newUserData.correo.toLowerCase();
            const { user } = await createUserWithEmailAndPassword(auth, lowercaseEmail, newUserData.password);
            const username = lowercaseEmail.split('@')[0];

            await updateDoc(doc(db, 'servicios', currentService.id), {
                users: arrayUnion(username),
                admins: arrayUnion(newUserData.correo),
            });

            const adminDocRef = await addDoc(
                collection(db, 'servicios', currentService.id, 'admins'),
                {
                    correo: newUserData.correo,
                    nombre: newUserData.nombre,
                    apellido: newUserData.apellido,
                    tipoUsuario: newUserData.tipoUsuario,
                    eliminado: false,
                    clientes: newUserData.clientes,
                    tiendas: newUserData.tiendas,
                    userId: user.uid,
                    provincias: newUserData.provincias,
                    comunidades: newUserData.comunidades,
                }
            );

            await updateClientesYTiendas(adminDocRef.id, newUserData.tipoUsuario);
            alert('Usuario añadido correctamente');
        } catch (error) {
            console.error('Error al añadir el usuario:', error);
            throw error;
        }
    };

    const updateClientesYTiendas = async (userId, tipoUsuario) => {
        const esAdministrativo = tipoUsuario.includes('administrativo');
        const esInspector = tipoUsuario.includes('inspector');

        for (const clienteId of newUserData.clientes) {
            const clienteRef = doc(db, 'servicios', currentService.id, 'clientes', clienteId);
            if (esAdministrativo) {
                await updateDoc(clienteRef, { administrativos: arrayUnion(userId) });
            }
            if (esInspector) {
                await updateDoc(clienteRef, { inspectores: arrayUnion(userId) });
            }
        }

        for (const tiendaId of newUserData.tiendas) {
            const tiendaRef = doc(db, 'servicios', currentService.id, 'tiendas', tiendaId);
            if (esAdministrativo) {
                await updateDoc(tiendaRef, { administrativos: arrayUnion(userId) });
            }
            if (esInspector) {
                await updateDoc(tiendaRef, { inspectores: arrayUnion(userId) });
            }
        }
    };

    const handleChangeClientes = (selectedClientes) => {
        const nuevasTiendasFiltradas = tiendas.filter(tienda =>
            selectedClientes.some(clienteId => tienda?.cliente === clienteId)
        );

        setTiendasFiltradas(nuevasTiendasFiltradas);
        setNewUserData(prevState => ({
            ...prevState,
            clientes: selectedClientes,
            tiendas: nuevasTiendasFiltradas.map(t => t.id)
        }));
    };

    const handleChangeTiendas = (selectedServicios) => {
        const tiendasIds = selectedServicios.map(numTienda => {
            const tienda = tiendas.find(t => t.numTienda.toString() === numTienda);
            return tienda ? tienda.id : numTienda;
        });

        setNewUserData({ ...newUserData, tiendas: tiendasIds });
    };

    const handleChangeComunidades = (event) => {
        const { value } = event.target;
        setComunidadesSeleccionadas(value);

        const provinciasDisponibles = value.reduce((acc, comunidad) => {
            const provinciasDeLaComunidad = provinciasPorComunidad[comunidad] || [];
            return [...acc, ...provinciasDeLaComunidad];
        }, []);

        const nuevasProvinciasSeleccionadas = provinciasSeleccionadas.filter(provincia =>
            provinciasDisponibles.includes(provincia)
        );

        const clientesFiltrados = clientes.filter(cliente => value.includes(cliente.comunidad));
        setClientesFiltrados(clientesFiltrados);
        setProvinciasSeleccionadas(nuevasProvinciasSeleccionadas);
        setNewUserData({
            ...newUserData,
            comunidades: value,
            provincias: nuevasProvinciasSeleccionadas
        });
    };

    const handleChangeProvincias = (selectedProvincias) => {
        setProvinciasSeleccionadas(selectedProvincias);
        setNewUserData({ ...newUserData, provincias: selectedProvincias });
    };

    // Filtrar clientes basados en comunidades seleccionadas
    const clientesFiltradosPorComunidad = useMemo(() => {
        return clientes.filter(cliente => {
            if (comunidadesSeleccionadas.length === 0) return true;
            return cliente.comunidad && comunidadesSeleccionadas.includes(cliente.comunidad);
        });
    }, [clientes, comunidadesSeleccionadas]);

    // Preparar datos de servicios para ServicioSearch
    const serviciosParaSelect = useMemo(() => {
        return tiendasFiltradas.map(tienda => ({
            numTienda: tienda.numTienda,
            nombreTienda: tienda.nombreTienda || `Servicio ${tienda.numTienda}`,
            nombreCliente: tienda.nombreCliente || clientes.find(c => c.id === tienda.cliente)?.nombre || 'Cliente desconocido'
        }));
    }, [tiendasFiltradas, clientes]);

    if (isLoading) {
        return (
            <div className="add-user-container">
                <h1>Añadir Nuevo Usuario</h1>
                <div className="form-container">
                    {[...Array(6)].map((_, index) => (
                        <Skeleton key={index} variant="rectangular" height={56} sx={{ mb: 2 }} />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="add-user-container">
            <h1>Añadir Nuevo Usuario</h1>
            
            {/* Mostrar estadísticas */}
            <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                    Total de clientes: {estadisticas.totalClientes.toLocaleString()} | 
                    Total de servicios: {estadisticas.totalTiendas.toLocaleString()}
                </Typography>
            </Alert>

            <div className="form-container">
                <div className="form-control">
                    <TextField
                        label="Nombre"
                        value={newUserData.nombre}
                        onChange={(e) => setNewUserData({ ...newUserData, nombre: e.target.value })}
                    />
                </div>
                <div className="form-control">
                    <TextField
                        label="Apellido"
                        value={newUserData.apellido}
                        onChange={(e) => setNewUserData({ ...newUserData, apellido: e.target.value })}
                    />
                </div>
                <div className="form-control">
                    <TextField
                        label="Correo"
                        value={newUserData.correo}
                        onChange={(e) => setNewUserData({ ...newUserData, correo: e.target.value })}
                    />
                </div>
                <div className="form-control">
                    <TextField
                        label="Contraseña"
                        type="password"
                        value={newUserData.password}
                        onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                    />
                </div>
                
                <FormControl fullWidth>
                    <InputLabel id="tipoUsuario-label">Tipo de Usuario</InputLabel>
                    <Select
                        labelId="tipoUsuario-label"
                        id="tipoUsuario"
                        multiple
                        value={newUserData.tipoUsuario}
                        onChange={(e) => setNewUserData({ ...newUserData, tipoUsuario: e.target.value })}
                        label="Tipo de Usuario"
                    >
                        <MenuItem value="administrador">Administrador</MenuItem>
                        <MenuItem value="administrativo">Administrativo</MenuItem>
                        <MenuItem value="inspector">Inspector</MenuItem>
                    </Select>
                </FormControl>

                <ProvinciasSelect
                    comunidadesSeleccionadas={comunidadesSeleccionadas}
                    provinciasSeleccionadas={provinciasSeleccionadas}
                    onSelectProvincias={handleChangeProvincias}
                />

                <FormControl fullWidth>
                    <InputLabel id="comunidades-label">Comunidades</InputLabel>
                    <Select
                        labelId="comunidades-label"
                        id="comunidades"
                        multiple
                        value={comunidadesSeleccionadas}
                        onChange={handleChangeComunidades}
                        label="Comunidades"
                    >
                        {comunidades.map((comunidad) => (
                            <MenuItem key={comunidad} value={comunidad}>
                                {comunidad}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Búsqueda optimizada de clientes */}
                <FormControl fullWidth>
                    <TextField
                        label="Buscar clientes"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            endAdornment: isSearching && <CircularProgress size={20} />
                        }}
                        helperText={`${clientesFiltrados.length} clientes encontrados`}
                        placeholder="Escribe para buscar clientes..."
                    />
                </FormControl>

                {/* Selección de clientes optimizada */}
                <FormControl fullWidth>
                    <InputLabel id="clientes-label">Clientes</InputLabel>
                    <Select
                        labelId="clientes-label"
                        id="clientes"
                        multiple
                        value={newUserData.clientes}
                        onChange={(e) => handleChangeClientes(e.target.value)}
                        label="Clientes"
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => {
                                    const cliente = clientesFiltrados.find(c => c.id === value);
                                    return (
                                        <Chip 
                                            key={value} 
                                            label={cliente ? cliente.nombre : value} 
                                            size="small" 
                                        />
                                    );
                                })}
                            </Box>
                        )}
                    >
                        {clientesFiltrados.slice(0, 100).map((cliente) => (
                            <MenuItem key={cliente.id} value={cliente.id}>
                                <Checkbox checked={newUserData.clientes.indexOf(cliente.id) > -1} />
                                <ListItemText primary={cliente.nombre} />
                            </MenuItem>
                        ))}
                        {clientesFiltrados.length > 100 && (
                            <MenuItem disabled>
                                <Typography variant="caption" color="textSecondary">
                                    Mostrando 100 de {clientesFiltrados.length} clientes. Usa la búsqueda para encontrar más.
                                </Typography>
                            </MenuItem>
                        )}
                        {hasMoreClientes && (
                            <MenuItem onClick={loadMoreClientes} disabled={isLoadingMore}>
                                {isLoadingMore ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <CircularProgress size={16} sx={{ mr: 1 }} />
                                        <Typography variant="caption">Cargando...</Typography>
                                    </Box>
                                ) : (
                                    <Typography variant="caption" color="primary">
                                        Cargar más clientes...
                                    </Typography>
                                )}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>

                {/* Selección de servicios optimizada */}
                <FormControl fullWidth>
                    <InputLabel id="tiendas-label">Servicios</InputLabel>
                    <Select
                        labelId="tiendas-label"
                        id="tiendas"
                        multiple
                        value={newUserData.tiendas.map(id => {
                            const tienda = tiendas.find(t => t.id === id);
                            return tienda ? tienda.numTienda.toString() : id;
                        })}
                        onChange={handleChangeTiendas}
                        label="Servicios"
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                    <Chip key={value} label={`Servicio ${value}`} size="small" />
                                ))}
                            </Box>
                        )}
                    >
                        {serviciosParaSelect.slice(0, 100).map((servicio) => (
                            <MenuItem key={servicio.numTienda} value={servicio.numTienda.toString()}>
                                <Checkbox checked={newUserData.tiendas.some(id => {
                                    const tienda = tiendas.find(t => t.id === id);
                                    return tienda && tienda.numTienda.toString() === servicio.numTienda.toString();
                                })} />
                                <ListItemText 
                                    primary={`Servicio ${servicio.numTienda}`}
                                    secondary={servicio.nombreCliente}
                                />
                            </MenuItem>
                        ))}
                        {serviciosParaSelect.length > 100 && (
                            <MenuItem disabled>
                                <Typography variant="caption" color="textSecondary">
                                    Mostrando 100 de {serviciosParaSelect.length} servicios.
                                </Typography>
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </div>
            
            <Button 
                onClick={handleAddUser} 
                variant="contained" 
                color="primary" 
                style={{ margin: '20px' }}
                disabled={isLoadingUser}
            >
                {isLoadingUser ? 'Añadiendo usuario...' : 'Añadir Usuario'}
            </Button>
            
            {isLoadingUser && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '10px' }}>
                    <CircularProgress size={20} style={{ marginRight: '10px' }} />
                    <p>Añadiendo usuario...</p>
                </div>
            )}
        </div>
    );
};

export default AddUser;